import {
  IonCard,
  IonIcon,
  IonSegment,
  IonSegmentButton,
  IonToggle
} from "@ionic/react";

import { lockClosedOutline } from "ionicons/icons";
import { get, shuffle } from "lodash";
import { useState } from "react";
import { Bot } from "../types/bot.type";
import { useGlobalsStore } from "../stores/globals.store";
import { globalWhitelist } from "../configs/whitelist";
import FavouriteBots from "./FavouriteBots";
import GlobalConfig from "./GlobalConfig";
import BuyConfig from "./BuyConfig";
import SellConfig from "./SellConfig";
import HelpButton from "./HelpButton";
import RangeSelector from "./RangeSelector";

const riskLevels: any = [
  {
    label: "Very Conservative",
    color: "text-black",
    "bg-color": "bg-blue-500"
  },
  {
    label: "Very Conservative",
    color: "text-black",
    "bg-color": "bg-emerald-500"
  },
  {
    label: "Conservative",
    color: "text-black",
    "bg-color": "bg-green-500"
  },
  {
    label: "Conservative",
    color: "text-black",
    "bg-color": "bg-lime-500"
  },
  {
    label: "Balanced",
    color: "text-black",
    "bg-color": "bg-slate-500"
  },
  {
    label: "Balanced",
    color: "text-black",
    "bg-color": "bg-amber-500"
  },
  {
    label: "Aggressive",
    color: "text-black",
    "bg-color": "bg-orange-500"
  },
  {
    label: "Aggressive",
    color: "text-black",
    "bg-color": "bg-red-500"
  },
  {
    label: "Very Aggressive",
    color: "text-black",
    "bg-color": "bg-red-600"
  }
];

const frequencyLevels: any = [
  {
    label: "Seldom",
    color: "text-black",
    "bg-color": "bg-blue-500"
  },
  {
    label: "Seldom",
    color: "text-black",
    "bg-color": "bg-emerald-500"
  },
  {
    label: "Occasionally",
    color: "text-black",
    "bg-color": "bg-green-500"
  },
  {
    label: "Conservative",
    color: "text-black",
    "bg-color": "bg-lime-500"
  },
  {
    label: "Sometimes",
    color: "text-black",
    "bg-color": "bg-slate-500"
  },
  {
    label: "Often",
    color: "text-black",
    "bg-color": "bg-amber-500"
  },
  {
    label: "Frequently",
    color: "text-black",
    "bg-color": "bg-orange-500"
  },
  {
    label: "Frequently",
    color: "text-black",
    "bg-color": "bg-red-500"
  },
  {
    label: "Very Frequently",
    color: "text-black",
    "bg-color": "bg-red-600"
  }
];

export const BasicStrategy: React.FC<{
  bot: Bot;
  setBot: any;
}> = ({ bot, setBot }) => {
  // eslint-disable-next-line
  const [experienceSegment, setExperienceSegment] = useState("newbie");
  const [strategySegment, setStrategySegment] = useState("custom");
  const [activeSegment, setActiveSegment] = useState("global");
  const [riskLevel, setRiskLevel] = useState(
    bot?.tradeParameters?.riskLevel || 0
  );
  const [frequencyLevel, setFrequencyLevel] = useState(
    bot?.tradeParameters?.tradeFrequency || 0
  );
  const strategies = useGlobalsStore(state => state?.globals?.strategies);

  const getStrategy = (name: string, type: string) => {
    return strategies.find(
      (strategy: any) => strategy.name === name && strategy.type === type
    );
  };
  const segments = [
    { value: "global", text: "Global", disabled: false },
    { value: "buy", text: "Buy", disabled: false },
    { value: "sell", text: "Sell", disabled: false }
  ];

  const riskFactorChanged = (riskFactor: number) => {
    setRiskLevel(riskFactor);

    const rsiStrategy = {
      name: "rsi",
      operator: "and",
      threshold: getStrategy("rsi", "buy").parameters[0].default
    };

    if (riskFactor !== 0) {
      let amplification = 1.5;
      if (riskFactor > 0) {
        amplification = 2.5;
      }
      rsiStrategy.threshold =
        rsiStrategy.threshold + riskFactor * amplification;
    }

    let buyStrategies = get(bot, "tradeParameters.buy.strategies", []);
    const index = buyStrategies.findIndex(
      (strategy: any) => strategy.name === "rsi"
    );
    if (index === -1) {
      buyStrategies = [rsiStrategy];
    } else {
      buyStrategies = buyStrategies.map((strategy: any, i: number) =>
        i === index ? rsiStrategy : strategy
      );
    }
    setBot({
      ...bot,
      tradeParameters: {
        ...bot.tradeParameters,
        tradeFrequency: frequencyLevel,
        riskLevel: riskFactor,
        buy: {
          ...bot.tradeParameters.buy,
          strategies: buyStrategies
        }
      }
    });
  };

  const tradeFrequencyFactorChanged = (tradeFrequencyFactor: number) => {
    setFrequencyLevel(tradeFrequencyFactor);
    // WHITELIST
    const numberOfCoins = 5 + 1 * tradeFrequencyFactor;
    const coinList = ["BTC", "BNB", "ETH"];
    // TODO: MANAGE WHITELIST
    shuffle(globalWhitelist).forEach(coin => {
      if (coinList.length < numberOfCoins && coinList.indexOf(coin) === -1) {
        coinList.push(coin.toUpperCase());
      }
    });

    // TRADE_SLOTS
    let tradeSlots = Math.round((5 * (tradeFrequencyFactor + 5)) / 12);
    if (tradeSlots === 0) {
      tradeSlots = 1;
    }
    setBot({
      ...bot,
      whitelist: coinList,
      tradeParameters: {
        ...bot.tradeParameters,
        tradeFrequency: tradeFrequencyFactor,
        riskLevel,
        buy: {
          ...bot.tradeParameters.buy,
          maxOpenTrades: tradeSlots
        }
      }
    });
  };

  return (
    <>
      <IonCard className="bg-primary-70 p-4">
        <div className="flex flex-row justify-between items-center">
          <div className="text-2xl font-semibold text-white">Set Strategy</div>
          <HelpButton id="strategy_overview" />
        </div>
        <IonSegment
          mode="ios"
          value={strategySegment}
          onIonChange={e => setStrategySegment(e.detail.value!)}
          className="bg-black"
        >
          <SegmentButton
            value="custom"
            label="Custom"
            selected={strategySegment === "custom"}
          />
          <SegmentButton
            value="saved"
            label="Saved"
            selected={strategySegment === "saved"}
          />
        </IonSegment>
        {strategySegment !== "saved" && (
          <>
            <p className="text-white text-lg py-2">View</p>
            <IonSegment
              mode="ios"
              value={experienceSegment}
              className="bg-black"
              onIonChange={(e: any) => setExperienceSegment(e.detail.value!)}
            >
              <SegmentButton
                value="newbie"
                label="Newbie"
                selected={experienceSegment === "newbie"}
              />
              <SegmentButton
                value="skillful"
                label="Skillful"
                selected={experienceSegment === "skillful"}
              />
              <SegmentButton
                value="expert"
                label="Expert"
                selected={experienceSegment === "expert"}
                locked
              />
            </IonSegment>
          </>
        )}

        {strategySegment === "custom" && (
          <>
            {experienceSegment === "newbie" && (
              <>
                <RangeSelector
                  title="Risk Level"
                  min={-4}
                  max={4}
                  step={1}
                  value={riskLevel}
                  onValueChange={value => riskFactorChanged(value)}
                  badgeLevels={riskLevels}
                  badgeLevel={riskLevel + 4}
                  helpId="risk_level"
                  isEnabled
                />
                <RangeSelector
                  title="Trade Frequecy"
                  min={-4}
                  max={4}
                  step={1}
                  value={frequencyLevel}
                  onValueChange={value => tradeFrequencyFactorChanged(value)}
                  badgeLevels={frequencyLevels}
                  badgeLevel={frequencyLevel + 4}
                  helpId="trading_frequency"
                  isEnabled
                />
              </>
            )}
            {experienceSegment !== "newbie" && (
              <div>
                <IonSegment
                  scrollable
                  className="bg-black mt-8 scrollable space-x-4 overflow-x-auto scrollbar-hide rounded-lg"
                  value={activeSegment}
                  onIonChange={event =>
                    setActiveSegment(event.detail.value || "global")
                  }
                >
                  {segments.map(segment => (
                    <IonSegmentButton
                      key={segment.value}
                      mode="md"
                      className={`text-[10px] bg-transparent whitespace-nowrap ${
                        activeSegment === segment.value
                          ? "border-b-4 border-primary-50"
                          : "text-primary-50"
                      }`}
                      value={segment.value}
                      onClick={() => setActiveSegment(segment.value)}
                      disabled={segment.disabled}
                    >
                      {segment.text}
                    </IonSegmentButton>
                  ))}
                </IonSegment>
                {activeSegment === "global" && (
                  <GlobalConfig bot={bot} updateBot={bot => setBot(bot)} />
                )}
                {activeSegment === "buy" && (
                  <BuyConfig bot={bot} updateBot={bot => setBot(bot)} />
                )}
                {activeSegment === "sell" && (
                  <SellConfig bot={bot} updateBot={bot => setBot(bot)} />
                )}
              </div>
            )}
          </>
        )}
        {strategySegment === "saved" && (
          <FavouriteBots
            onSelect={selectedBot => {
              setBot({
                ...bot,
                whitelist: selectedBot.whitelist,
                tradeParameters: {
                  ...selectedBot.tradeParameters,
                  quoteAsset: selectedBot.quoteAsset
                }
              });
              setStrategySegment("custom");
            }}
            selectedBots={[]}
          />
        )}
      </IonCard>
      {bot?.mode === "simulation" && (
        <>
          {/* <div className="text-2xl p-2 pb-4 flex items-center">
            <IonIcon icon={settingsOutline} className="mr-2" />
            Settings
          </div> */}
          <div className="h-[2px] my-8 bg-primary-70"></div>
          <div className="flex items-center justify-between px-4 text-white">
            <div className="w-60">
              <p className="text-lg font-semibold mb-2">Notifications</p>
              <p className="text-sm">
                Receive updates on your Bots trading activity and performance
              </p>
            </div>
            <div>
              <IonToggle
                checked={bot?.notifications}
                onIonChange={e =>
                  setBot({ ...bot, notifications: e.detail.checked })
                }
              />
            </div>
          </div>
          <div className="flex items-center justify-between px-4 text-white mt-8">
            <div className="w-60">
              <p className="text-lg font-semibold mb-2">Privacy</p>
              <p className="text-sm">
                Restrict other players from publically viewing your bot
              </p>
            </div>
            <div>
              <IonToggle
                checked={bot?.private}
                onIonChange={e => setBot({ ...bot, private: e.detail.checked })}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};

const SegmentButton: React.FC<{
  locked?: boolean;
  label: string;
  selected?: boolean;
  [x: string]: any;
}> = ({ locked = false, label, ...props }) => {
  return (
    <IonSegmentButton disabled={locked} {...props} className="py-2">
      <div
        className={`${!props?.selected && "text-primary-60"} flex items-center`}
      >
        {locked && <IonIcon className="px-2" icon={lockClosedOutline} />}
        {label}
      </div>
    </IonSegmentButton>
  );
};
